import React from 'react';
import { Box, Stack, Typography, styled } from '@mui/material';
import { SvgIcon } from 'components/SvgIcon/SvgIcon';
import { ReactComponent as ChatWithCareTeamIcon } from 'assets/icons/ChatWithCareTeam.svg';
import { ReactComponent as CheckInSurveysIcon } from 'assets/icons/WhatToExpect/CheckInSurveys.svg';
import { ReactComponent as CheckInViewProgressIcon } from 'assets/icons/WhatToExpect/CheckInViewProgress.svg';
import { ReactComponent as DaysOfContentIcon } from 'assets/icons/WhatToExpect/DaysOfContent.svg';
import { ReactComponent as EducationalArticlesIcon } from 'assets/icons/WhatToExpect/EducationalArticles.svg';
import { Button } from 'components/Button/Button';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { getHealthTopicIcon } from 'features/resources/utils/healthTopicUtils';
import type { CareProgramDetail, WhatToExpectItem } from 'features/resources/types';
import { CareProgramStatusPill } from 'components/CareProgramStatusPill/CareProgramStatusPill';

const ContentContainer = styled(Box)(({ theme }) => ({
  flexDirection: 'column',
  display: 'flex',
  justifyContent: 'space-between',
  gap: theme.spacing(3),
  overflowY: 'auto',
  height: '100%',
  paddingBlock: theme.spacing(3),
  paddingInline: theme.spacing(10),
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(10),
    paddingInline: theme.spacing(15),
  },
  [theme.breakpoints.down('sm')]: {
    paddingInline: theme.spacing(5),
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.brandShade2.main,
  color: theme.palette.common.white,
}));

const StatusPillWrapper = styled(Box)(() => ({
  alignSelf: 'flex-start',
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  flex: 2,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
  },
}));

export interface CareProgramDetailContentProps {
  program?: CareProgramDetail;
  getItemText: (item: WhatToExpectItem) => string;
  showChatIcon?: boolean;
  onClickEnroll?: () => void;
  enrollButtonDisabled?: boolean;
  enrollmentButtonText: string;
  statusLabel?: string;
}

const getWhatToExpectIcon = (type: WhatToExpectItem['type']): React.ComponentType => {
  switch (type) {
    case 'checkInViewProgress':
      return CheckInViewProgressIcon;
    case 'checkIn':
      return CheckInSurveysIcon;
    case 'educationalArticles':
      return EducationalArticlesIcon;
    case 'daysOfContent':
    default:
      return DaysOfContentIcon;
  }
};

export const CareProgramDetailContent = ({
  program,
  getItemText,
  enrollmentButtonText,
  showChatIcon,
  onClickEnroll,
  enrollButtonDisabled,
  statusLabel,
}: CareProgramDetailContentProps) => {
  const { translation } = useTranslationContext();
  const IconComponent = getHealthTopicIcon(program?.healthTopic ?? 'cancer');
  return (
    <ContentContainer>
      <Stack spacing={3}>
        <SvgIcon
          sx={{ fontSize: 100 }}
          inheritViewBox
          component={IconComponent}
          aria-hidden="true"
        />
        <Stack spacing={1}>
          <Typography fontWeight={500} variant="title2" className="care-program-detail-page__title">
            {program?.title}
          </Typography>
          <Typography variant="callout" className="care-program-detail-page__summary">
            {program?.summary}
          </Typography>
        </Stack>
        {statusLabel && (
          <StatusPillWrapper className="care-program-detail-page__status">
            <CareProgramStatusPill label={statusLabel} />
          </StatusPillWrapper>
        )}
        {program && program?.whatToExpect?.length > 0 && (
          <Stack spacing={1.5} className="care-program-detail-page__what-to-expect">
            <Typography fontWeight={500} variant="callout" id="care-program-what-to-expect-heading">
              {translation('what_to_expect')}
            </Typography>
            {program.whatToExpect.map((item) => (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <SvgIcon
                  component={getWhatToExpectIcon(item.type)}
                  aria-hidden="true"
                  sx={{ fontSize: 20 }}
                />
                <Typography variant="callout" color="grey.800">
                  {getItemText(item)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
      <ButtonContainer>
        <StyledButton
          size="xlarge"
          fullWidth
          className="care-program-detail-page__enroll-button"
          onClick={onClickEnroll}
          disabled={enrollButtonDisabled}
          startIcon={showChatIcon ? <ChatWithCareTeamIcon width={20} height={20} /> : undefined}
        >
          {enrollmentButtonText}
        </StyledButton>
      </ButtonContainer>
    </ContentContainer>
  );
};
